<template>
  <div
    :data-group-name="name"
    @dragover.prevent
    @drop.prevent="handleDrop($event, 'intf', app.appID)"
    >
    <div
      class="d-flex"
      >
      <v-text-field
        dense
        :id="groupName"
        v-model="groupName"
        title="Change group name"
        :readonly="!renameGroupStatus"
        @keydown.enter="renameGroup"
        @dblclick.native="toggleRenameGroup"
        hide-details="auto"
        class="build-group__app-name h2 ma-4"
      />
      <div class="d-flex align-center content-editable__actions">
        <apio-icon
          v-if="!renameGroupStatus"
          class="build-group__pointer"
          name="ui-edit"
          title="Edit Name"
          @click.native="toggleRenameGroup"
        >
        </apio-icon>
        <div class="build-group__save-cancel-name-edit">
          <apio-icon
            v-if="renameGroupStatus"
            class="build-group__pointer mr-2"
            name="ui-save"
            title="Save name"
            @click.native="renameGroup"
          />
          <apio-icon
            v-if="renameGroupStatus"
            name="ui-circle-cross-line"
            class="build-group__pointer"
            title="Cancel editing"
            @click.native="toggleRenameGroup"
          >
          </apio-icon>
        </div>
      </div>
      <apio-button
      button-size="small"
      button-type="secondary"
      class="ml-4 mt-4 pa-2 pr-3"
      @click.native="toggleNewInterfaceModal('interfaces')">
          <apio-icon
              name="ui-plus"
              class="mr-2"
              size="16"
              lightModeColour="cyan"
              darkModeColour="white"
          />
      ADD INTERFACE
      </apio-button>
        <apio-button
          v-if="appData.app_groups && Object.keys(appData.app_groups).length > 1"
          class="mt-4 ml-2"
          button-text-size="tiny"
          button-size="small"
          button-type="secondary"
          @click="jsDeleteGroup"
        >
          <apio-icon
            lightModeColour="cyan"
            darkModeColour="white"
            name="ui-trash"
        /></apio-button>
    </div>

    <div
      v-if="interfacesForGroup.length === 0"
      class="build-group__no-interfaces-defined my-4"
    >
      You haven’t created any interfaces for this group, please add one. <apio-button
      button-size="small"
      button-type="secondary"
      class="mt-4 pa-2 pr-3"
      @click.native="toggleNewInterfaceModal('interfaces')">
          <apio-icon
              name="ui-plus"
              class="mr-2"
              size="16"
              lightModeColour="cyan"
              darkModeColour="white"
          />
      ADD INTERFACE
      </apio-button>
    </div>

    <div
      v-for="(item, i) in interfacesForGroup"
      class="build-group__item"
      :class="{ 'build-group__item--dark': isDarkMode }"
      :key="i"
      @dragstart="dragStart($event, 'interfaces', appData.appID, name)"
      :draggable="true"
      :id="item.name"
      :data-name="item.real_name"
      :data-parent-id="appData.appID"
      :data-item-index="i"
    >
      <div class="build-group__item-description">
        <Help article="build/interface/name" />
        <apio-icon
          size="20"
          name="ui-drag"
          lightModeColour="apioshade2"
          darkModeColour="white"
        />
        <img
          v-if="item.input_type"
          class="mr-2"
          height="32"
          :src="'/img/icons/input_' + item.input_type + '.svg'"
          :alt="item.input_type"
          :title="'Input: '+item.input_type + ' - '+item.hash"
        />
        <apio-icon
          v-else
          class="mr-2"
          size="32"
          name="ui-gear"
          lightModeColour="apioshade3"
          darkModeColour="apioshade3"
          :title="item.hash"
        />

        <v-text-field
          class="build-group__interface-name mr-4"
          v-if="editInterfaceLabels"
          v-model="item.real_name"
          outlined
          dense
          hide-details="auto"
          @input="renameInterface(item, item.real_name)"
        />

        <span
          v-if="!editInterfaceLabels"
          @click="selectConfEdit(appData, item)"
          class="p2-extra-bold mr-1"
          >{{ item.real_name }}</span
        >
        <div
          class="flex flex-grow-1"
          style="height: 100%"
          @click="selectConfEdit(appData, item)"
        >
          <v-spacer />
        </div>

        <build-list-item-menu
          v-if="$mq !== 'xlarge' && $mq !== 'xxlarge'"
          @save-as-recipe="toggleSaveAsRecipeDialog(item)"
          @edit-build="selectConfEdit(appData, item)"
          @copy-interface="copyInterface(item)"
          @delete-interface="deleteInterfaceModal(item, 'interfaces')"
        />
      </div>
      <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <v-icon v-bind="attrs" v-on="on" class="mr-6">mdi-information</v-icon>
      </template>
      <div>
        <div><b>Modified by:</b> {{item.last_modifier}}</div>
        <div><b>Updated at:</b> {{item.updated}}</div>
      </div>
      </v-tooltip>
      <div
        v-if="
          ($mq === 'xlarge' || $mq === 'xxlarge') &&
          unpublishedChanges(item.updated)
        "
        class="d-flex tiny mr-6 align-center"
        style="min-width: 124px"
      >
        Unpublished changes
        <apio-icon
          size="20"
          class="ml-1"
          name="ui-warning-circle"
          lightModeColour="warning"
          darkModeColour="warning"
        />
      </div>
      <div class="build-group__item-details">
        <div
          v-if="
            ($mq === 'small' || $mq === 'medium' || $mq === 'large') &&
            unpublishedChanges(item.updated)
          "
          class="d-flex tiny mr-6 align-center"
          style="min-width: 124px"
        >
          Unpublished changes
          <apio-icon
            size="20"
            class="ml-1"
            name="ui-warning-circle"
            lightModeColour="warning"
            darkModeColour="warning"
          />
        </div>
        <apio-switch
          :color="'green'"
          :value="item.enabled"
          @change="toggleInterfaceStatus(item, $event)"
        >
          <span slot="left-label" class="tiny">Active</span>
        </apio-switch>
        <build-list-item-menu
          v-if="$mq === 'xlarge' || $mq === 'xxlarge'"
          @save-as-recipe="toggleSaveAsRecipeDialog(item)"
          @edit-build="selectConfEdit(appData, item)"
          @copy-interface="copyInterface(item)"
          @delete-interface="deleteInterfaceModal(item, 'interfaces')"
        />
      </div>
      <Help article="build/interface/actions" />
    </div>

    <v-dialog persistent v-model="addInterfaceStatus" width="600">
      <v-card>
        <v-toolbar flat color="apioshade3" class="white--text h2 mb-6">
          <div class="white--text run-step__title">
            Add new interface
          </div>
          <v-spacer></v-spacer>
          <apio-button
            button-type="get-started"
            button-size="small"
            @click="addInterfaceStatus = false"
            >Close
            <apio-icon
              class="ml-2"
              size="16"
              name="cross"
              lightModeColour="white"
              darkModeColour="white"
          /></apio-button>
        </v-toolbar>
        <v-card-text class="pb-0">
          <validation-observer
            tag="form"
            ref="new"
            @submit.prevent="validateForm('new')"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="required"
              name="interface name"
              tag="div"
            >
              <label for="interface-name">
                New interface name
              </label>
              <v-text-field
                id="interface-name"
                outlined
                dense
                hide-details="auto"
                required
                placeholder="Enter interface name"
                v-model="interfaceName"
                ref="inputModalInterface"
              />
              <transition name="fade">
                <span v-if="errors[0]">{{ errors[0] }}</span>
              </transition>
            </validation-provider>
          </validation-observer>
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <apio-button
            button-type="secondary"
            button-size="small"
            class="mr-2"
            @click="toggleNewInterfaceModal()"
            >Cancel</apio-button
          >
          <apio-button button-size="small" @click="validateForm('new')"
            >Create</apio-button
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="addInterfaceFromRecipeStatus" fullscreen hide-overlay>
      <div class="recipe-dialog">
        <apio-button
          button-type="selected"
          class="recipe-dialog__header-close"
          @click="addInterfaceFromRecipeStatus = false"
          :button-size="recipeDialogHeaderButtonCloseSize"
        >
          Close
          <apio-icon
            name="cross"
            class="ml-2"
            size="16"
            lightModeColour="white"
            darkModeColour="white"
          />
        </apio-button>
        <div class="recipe-dialog__header">
          <div class="d-flex align-center">
            <div>
              <apio-icon
                class="recipe-dialog__header-icon"
                name="ui-gear-only"
                :size="recipeDialogHeaderIconSize"
                lightModeColour="white"
                darkModeColour="white"
              />
            </div>
            <div class="recipe-dialog__header-text">Available recipes</div>
          </div>
          <div class="recipe-dialog__blurb">
            At vero eos et accusamus et iusto odio dignissimos ducimus qui
            blanditiis praesentium voluptatum deleniti atque corrupti quos
            dolores et quas molestias excepturi sint occaecati cupiditate non
            provident.
          </div>
        </div>
        <AddInterface :parent="appData.appID" @recipeadd="recipeadd" />
      </div>
    </v-dialog>
    <v-dialog max-width="600" persistent v-model="deleteModalStatus">
      <v-card>
        <v-toolbar flat color="apioshade3" class="white--text h2 mb-6">
          <div class="white--text run-step__title">
            Delete Interface
          </div>
          <v-spacer></v-spacer>
          <apio-button
            button-type="get-started"
            button-size="small"
            @click="deleteModalStatus = false"
            >Close
            <apio-icon
              class="ml-2"
              size="16"
              name="cross"
              lightModeColour="white"
              darkModeColour="white"
          /></apio-button>
        </v-toolbar>
        <v-card-text class="pb-0">
          <div>
            <p>
              Are you sure that you want to delete
              <span>{{
                interfaceToDelete.real_name
                  ? interfaceToDelete.real_name
                  : interfaceToDelete.name
              }}</span>
              <span>interface?</span>
            </p>
          </div>
        </v-card-text>

        <v-card-actions class="pa-6">
          <v-spacer />
          <apio-button
            button-size="small"
            button-type="secondary"
            class="mr-2"
            @click="deleteModalStatus = false"
            >Cancel</apio-button
          >
          <apio-button button-size="small" @click="deleteInterface()"
            >Delete</apio-button
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog max-width="500" v-model="bvModalSaveRecipe">
      <v-card>
        <v-toolbar flat color="apioshade3" class="white--text h2 mb-6">
          <div class="white--text run-step__title">Save as recipe</div>
          <v-spacer></v-spacer>
          <apio-button
            button-type="get-started"
            button-size="small"
            @click="bvModalSaveRecipe = false"
            >Close
            <apio-icon
              class="ml-2"
              size="16"
              name="cross"
              lightModeColour="white"
              darkModeColour="white"
          /></apio-button>
        </v-toolbar>
        <v-card-text>
          <v-text-field
            id="recipename"
            outlined
            dense
            hide-details="auto"
            required
            placeholder="Recipe Name"
            v-model="recipeName"
            ref="inputModalRecipeName"
          />
          <v-textarea
            placeholder="Write about the recipe's purpose and use cases"
            v-model="recipeDesc"
          />
        </v-card-text>
        <v-card-text color="red">{{ recipeNameError }}</v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <apio-button
            class="mr-2"
            button-type="secondary"
            @click="toggleSaveAsRecipeDialog"
            >Close</apio-button
          >
          <apio-button
            :disabled="!recipeName || !recipeDesc"
            @click="saveRecipe"
            >Save</apio-button
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <apio-snackbar
      :text="snackBarText"
      :status="snackBarStatus"
      :snackbar="snackbar"
      @close="snackbar = false"
    />
  </div>
</template>

<script>
import request from '@/API/';
import { debounce } from "lodash";
import editor from "@/API/services/editor";
import dayjs from "dayjs";
import Vue from 'vue';

export default {
  props: {
    name: {
      required: true,
    },
    interfaces: {},
    app: {
      required: true,
    },
    editInterfaceLabels: {
      type: Boolean,
      default: false,
    },
    applicationStatus: {

    }
  },
  watch: {
    app: {
      handler() {
        this.appData = this.app;
        this.interfacesForGroup = [];
        if(this.allInterfacesArray.length === 0) return;
        this.appData.app_groups[this.name].map((intf) => {
          console.log(this.interfaces[intf])
          if(this.interfaces[intf] && this.interfaces[intf].name) {
            this.interfacesForGroup.push(this.interfaces[intf]);
          }
        });
      },
      deep: true,
      immediate: true,
    }
  },
  data() {
    return {
      addInterfaceFromRecipeStatus: false,
      addInterfaceStatus: false,
      addNewGroupStatus: false,
      bvModalSaveRecipe: false,
      deleteModalStatus: false,
      existingInterfaceType: "",
      groupInterfaces: [],
      groupName: this.name,
      oldGroupName: this.name,
      interfaceName: "",
      interfaceToDelete: "",
      newInterfaceType: "",
      recipeDesc: "",
      recipeName: "",
      recipeNameError: "",
      renameGroupStatus: false,
      interfacesForGroup: [],
      appData: [],
      snackbar: false,
      snackBarStatus: "",
      snackBarText: "",
    };
  },
  mounted() {
    this.appData = this.app;
  },
  components: {
    AddInterface: () => import("@/components/Recipes/AddInterface.vue"),
    ApioSwitch: () => import("@/components/Misc/Switch.vue"),
    BuildListItemMenu: () =>
      import("@/components/BuildList/BuildListItemMenu.vue"),
    Help: () => import("@/components/Help/Help.vue"),
  },
  computed: {
    isDarkMode() {
      return this.$store.state.mode.darkMode;
    },
    recipeDialogHeaderButtonCloseSize() {
      return this.$mq === "xlarge" || this.$mq === "xxlarge"
        ? "medium"
        : "small";
    },
    recipeDialogHeaderIconSize() {
      return this.$mq === "xlarge" || this.$mq === "xxlarge" ? "40" : "24";
    },
    clientHash() {
      return this.$route.query.c;
    },
    allInterfacesArray() {
      return Object.keys(this.interfaces);
    },
    isRunning() {
      return this.applicationStatus.running;
    },
    isUpgrading() {
      return this.applicationStatus.upgrading;
    },
  },
  created() {
    this.renameInterface = debounce((currentInterface, newInterfaceName) => {
      this.$store.dispatch("renameInterface", {
        interface: currentInterface,
        interfaceName: currentInterface.name,
        newInterfaceName: newInterfaceName,
        clientHash: this.clientHash,
        appName: this.appData.real_name,
        appID: this.appData.appID,
      });
    }, 1500);

    this.renameInterface.bind(this);
  },
  methods: {
    jsDeleteGroup() {
      this.$root.$emit('deleteGroup', this.groupName)
    },
    checkIfGroupExists(groupName) {
      return Object.prototype.hasOwnProperty.call(this.appData.app_groups, groupName);
    },
    async renameGroup() {

      if(this.checkIfGroupExists(this.groupName)) {
        this.snackBarText = "Group name already in use";
        this.snackBarStatus = "error";
        this.snackbar = true;
        return
      }

      this.appData.app_groups[this.groupName] = this.appData.app_groups[this.oldGroupName]
      delete this.appData.app_groups[this.oldGroupName];

      await this.saveGroup(this.groupName);
      await this.deleteGroup(this.oldGroupName);

      this.toggleRenameGroup();
    },
    async deleteGroup(groupToDelete) {
      try {
        const payload = {
          "label": groupToDelete,
          clientHash: this.clientHash,
          appHash: this.appData.appID,
        }

        await editor.deleteGroup(payload);
      } catch (err) {
        console.log(err);
      }
    },
    toggleRenameGroup() {
      this.renameGroupStatus = !this.renameGroupStatus;
      if(this.renameGroupStatus) {
        document.getElementById(this.groupName).focus();
      }
    },
    dragStart(event, type, appID, groupName) {
      const target = event.target;

      event.dataTransfer.setData("card_id", target.id);
      event.dataTransfer.setData("card_name", target.name);
      event.dataTransfer.setData("card_type", type);
      event.dataTransfer.setData("source_app", appID)
      event.dataTransfer.setData("source_group", groupName);
    },
    removeInterfaceFromGroup(intf) {
      this.appData.app_groups[this.name] = this.appData.app_groups[this.name].filter(item => item !== intf)
      this.saveGroup();
    },
    swapInterfacePositionsInGroup(sourceIndex, targetIndex) {
      const interfaceA = this.interfacesForGroup[sourceIndex];
      const interfaceB = this.interfacesForGroup[targetIndex];
      Vue.set(this.interfacesForGroup, targetIndex, interfaceA);
      Vue.set(this.interfacesForGroup, sourceIndex, interfaceB);
      this.saveInterfacesForGroup();
    },
    async handleDrop(event, dropType) {
      if (dropType !== "intf") {
        return;
      }

      const interfaceHash = event.dataTransfer.getData("card_id");
      const type = event.dataTransfer.getData("card_type");
      const sourceAppHash = event.dataTransfer.getData("source_app");
      const item = document.getElementById(interfaceHash);
      const parentID = item.getAttribute("data-parent-id");
      const targetAppHash = event.path
        .find((item) => item.getAttribute("data-app-id"))
        .getAttribute("data-app-id");

      let targetItemIndex = 0;

      try {
        targetItemIndex = event.path
          .find((item) => item.getAttribute("data-itemIndex"))
          .getAttribute("data-item-index");
      } catch(e) {
        console.warn(e)
      }
      const itemIndex = item.getAttribute("data-item-index")

      //IF THE APPID IS DIFFERENT TO THE TARGET THEN COPY
      //ELSE CREATE

      const targetGroupName = event.path
        .find((item) => item.getAttribute("data-group-name"))
        .getAttribute("data-group-name");

      const sourceGroupName = event.dataTransfer.getData("source_group")

      if((sourceGroupName === targetGroupName) && (sourceAppHash === targetAppHash) && itemIndex != targetItemIndex) {
        this.swapInterfacePositionsInGroup(itemIndex, targetItemIndex);
      } else if(sourceAppHash && (targetAppHash !== sourceAppHash)) {
        const data = await this.$store.dispatch("dragInterface", {
          targetAppHash,
          interfaceHash,
          parentID,
          type,
        });

        const interface_hash = data?.interface_hash || interfaceHash;
        // PUSH NEW HASH INTO CORRECT APP AND APPGROUP USING THE targetGroupName AND targetAppHash
        this.appData.app_groups[targetGroupName].push(interface_hash);
        // SAVE GROUP HERE
        this.saveDnDGroup(targetAppHash, this.clientHash, targetGroupName);
      } else {
        //move interface to new group, remove from old group

        if(sourceGroupName != targetGroupName) {
          this.moveInterfaceBetweenGroups(interfaceHash, targetGroupName, sourceGroupName);
        }
      }
    },
    saveRecipe() {
      request
        .post("/editor/saverecipe?c=" + this.clientHash, {
          title: this.recipeName,
          description: this.recipeDesc,
          recipe: JSON.stringify(this.currentInterface),
        })
        .then((res) => {
          this.docs = res.data;
          this.bvModalSaveRecipe = false;
          this.currentInterface = null;
        })
        .catch((err) => {
          this.recipeNameError = err;
        });
    },
    toggleSaveAsRecipeDialog(item) {
      this.bvModalSaveRecipe = !this.bvModalSaveRecipe;
      this.bvModalSaveRecipe
        ? (this.currentInterface = item)
        : (this.currentInterface = null);
    },
    async recipeadd(selrec) {
      this.addInterfaceFromRecipeStatus = false;
      const hash = await this.$store.dispatch("createNewInterface", {
        name: selrec.recipe.title,
        type: this.newInterfaceType,
        clientHash: this.clientHash,
        appName: this.appData.real_name,
        appID: selrec.parent,
        content: selrec.recipe.recipe,
      });

      this.appData.app_groups[this.name].push(hash);
      this.saveGroup();
    },
    async validateForm(formName) {
      const valid = await this.$refs[formName].validate();

      if (valid) {
        switch (formName) {
          case "rename": {
            this.renameInterface();
            break;
          }
          case "new": {
            this.addNewInterface();
            break;
          }
          default: {
            break;
          }
        }
      }
    },
    toggleEditInterfaceLabels() {
      this.editInterfaceLabels = !this.editInterfaceLabels;
    },
    toggleInterfaceStatus(currentInterface) {
      currentInterface.enabled = !currentInterface.enabled;
      this.$store.dispatch("toggleInterfaceStatus", {
        interface: currentInterface,
        clientHash: this.clientHash,
        appID: this.appData.appID,
        newStatus: currentInterface.enabled,
      });
    },
    selectConfEdit(selectedApp, item) {
      this.$router.push({
        path: "/editinterface",
        query: {
          a: selectedApp.appID,
          i: item.hash,
          c: this.clientHash,
          e: selectedApp.live_env,
          al: item.real_name,
          il: item.real_name,
        },
      });
    },
    unpublishedChanges(interfaceUpdateTimestamp = dayjs.tz().utc().unix()) {
      if (this.isRunning) {
        const lastAppTime = this.applicationStatus?.item?.t;
        if (lastAppTime) {
          const lastAppTimeUnix = dayjs.tz(lastAppTime).utc().unix();
          const lastInterfaceTimeUnix = dayjs
            .tz(interfaceUpdateTimestamp)
            .utc()
            .unix();

          if (lastInterfaceTimeUnix > lastAppTimeUnix) {
            return true;
          }
        }
        return false;
      }
    },
    async moveInterfaceBetweenGroups(interfaceHash, targetGroupName, sourceGroupName) {
      // Remove from source group
      this.appData.app_groups[targetGroupName].push(interfaceHash);
      // Add to target group
      this.appData.app_groups[sourceGroupName] = this.appData.app_groups[sourceGroupName].filter(intf => intf !== interfaceHash);

      await this.saveGroup(targetGroupName, sourceGroupName);
      return
    },
    async addNewInterface() {
      const hash = await this.$store.dispatch("createNewInterface", {
        name: this.interfaceName,
        type: this.newInterfaceType,
        clientHash: this.clientHash,
        appName: this.appData.real_name,
        appID: this.appData.appID,
      });

      if(hash !== undefined) {
        this.saveGroup();
        setTimeout(this.appData.app_groups[this.groupName].push(hash), 200);
      }
      this.interfaceName = '';
      this.toggleNewInterfaceModal();
    },
    async copyInterface(item) {
      const hash = await this.$store.dispatch("copyInterface", {
        appID: this.appData.appID,
        interface: item,
        clientHash: this.clientHash,
        appName: item.real_name,
      });

      this.appData.app_groups[this.name].push(hash);
      this.saveGroup();
    },
    deleteInterface() {
      this.$store.dispatch("deleteInterface", {
        interface: this.interfaceToDelete,
        clientHash: this.clientHash,
        appName: this.appData.real_name,
        appID: this.appData.appID,
        app: this.app,
      });
      this.saveGroup();
      this.deleteModalStatus = false;
    },
    deleteInterfaceModal(item, type) {
      this.existingInterfaceType = type;
      this.deleteModalStatus = !this.deleteModalStatus;
      this.interfaceToDelete = item;
    },
    toggleNewInterfaceModal(type) {
      this.addInterfaceStatus = !this.addInterfaceStatus;
      this.newInterfaceType = type;
    },
    async saveDnDGroup(appHash, clientHash, groupName) {
       try {
        const payload = {
          clientHash,
          appHash,
          [groupName]: this.appData.app_groups[groupName],
        };
        await editor.saveGroup(payload);
        this.alertsDialog = false;

      } catch (err) {
        console.log(err);
      }
    },
    async saveInterfacesForGroup() {
      const x = Object.values(this.interfacesForGroup);

      const interfaces = x.map((intf) => intf.hash)

      const payload = {
        clientHash: this.clientHash,
        appHash: this.appData.appID,
        [this.name]: interfaces,
      };

      try {
        await editor.saveGroup(payload);
        this.$root.$emit('getApps');
        this.alertsDialog = false;
      } catch(e) {
        console.log(e);
      }

    },
    async saveGroup(groupName = this.name, sourceGroupName) {
      try {
        let payload;

        if(sourceGroupName) {
          payload = {
            clientHash: this.clientHash,
            appHash: this.appData.appID,
            sourceAndTarget: {
              [groupName]: this.appData.app_groups[groupName],
              [sourceGroupName]: this.appData.app_groups[sourceGroupName],
            },
          };
        } else {
          payload = {
            clientHash: this.clientHash,
            appHash: this.appData.appID,
            [groupName]: this.appData.app_groups[groupName],
          };
        }
        await editor.saveGroup(payload);
        this.alertsDialog = false;
        this.$root.$emit('getApps');
      } catch (err) {
        console.log(err);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .v-input__control,
::v-deep .v-input__slot,
::v-deep .v-text-field__slot {
  min-height: 32px !important;
  height: 32px;
}

.recipe-dialog {
  height: 100%;
  position: relative;
  padding: 14px 16px 16px;
  background-color: white;
  color: var(--apio-shade-2);

  &__blurb {
    margin-top: 24px;
  }

  &__header {
    $padding-y: 14px;
    $padding-x: 16px;
    margin-bottom: 8px;

    color: var(--apio-shade-2);
    padding: calc(100px - #{$padding-y}) calc(120px - #{$padding-x}) 16px
      calc(120px - #{$padding-x});
    @media (max-width: 1024px) {
      padding: calc(80px - #{$padding-y}) calc(20px - #{$padding-x});
    }
  }

  &__header-close {
    position: absolute;
    right: 14px;
    @media (max-width: 1024px) {
      right: 8px;
    }
  }

  &__header-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-color: var(--apio-shade-3);

    @media (max-width: 1024px) {
      height: 40px;
      width: 40px;
    }
  }

  &__header-text {
    font-size: var(--font-h1-size);
    font-weight: var(--font-semi-bold);
    margin-left: 16px;
    @media (max-width: 1024px) {
      font-size: var(--font-h2-size);
    }
  }
}

.build-group {
  padding: 24px 32px;
  @media (max-width: 1024px) {
    padding: 16px;
  }

  &__app-name {
    $text-field-outlined-fieldset-border-width: 0;
    max-width: 250px;
  }

  &__pointer {
    cursor: pointer;
  }

  &__save-cancel-name-edit {
    display: flex;
  }

  &__group-name {
    text-transform: capitalize;
  }

  &__no-interfaces-defined {
    background-color: #fff;
    border-radius: 2px;
    border: 2px dotted var(--apio-shade-4);
    color: var(--apio-shade-3);
    font-size: var(--font-h3-size);
    font-weight: var(--font-semi-bold);
    padding: 16px;
  }

  &__header {
    align-items: center;
    display: flex;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }

  &__header-item {
    align-items: center;
    color: var(--apio-cyan);
    cursor: pointer;
    display: flex;
    font-size: var(--font-small-size);
    font-weight: var(--font-extra-bold);

    &:not(last-child) {
      margin-right: 16px;
      @media (max-width: 508px) {
        margin-bottom: 16px;
      }
    }
  }

  &__item-description {
    display: flex;
    align-items: center;
    height: 48px;
    width: 100%;
  }

  &__item-details {
    display: flex;
    @media (max-width: 1024px) {
      border-top: 1px solid var(--apio-shade-4);
      display: flex;
      justify-content: flex-end;
      height: 48px;
      width: 100%;
    }
  }

  &__item {
    &:not(last-child) {
      margin-right: 16px;
      @media (max-width: 508px) {
        margin-bottom: 16px;
      }
    }

    &:nth-of-type(1) {
      &:before {
        height: 38px;
        left: -24px;
        top: -14px;

        @media (max-width: 1024px) {
          height: 32px;
          width: 8px;
          left: -9px;
          top: -8px;
        }
      }
    }

    align-items: center;
    background-color: var(--apio-shade-5);
    border-radius: 4px;
    border: 1px solid var(--apio-shade-4);
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    height: 48px;
    margin-bottom: 16px;
    margin-left: 24px;
    padding: 8px 4px;
    position: relative;

    &:last-child {
      margin-bottom: 0;
    }

    &--details {
      background-color: var(--apio-shade-5);
    }

    &--dark {
      background-color: var(--apio-shade-1);
    }

    @media (max-width: 1023px) {
      padding-left: 4px;
      flex-direction: column;
      align-items: start;
      height: 96px;
      margin: 8px 0 8px 8px;
    }
  }
}
</style>
